import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment.development';
import { ToastMessage } from '../Components/toast/toast.component';
import { Router } from '@angular/router';


@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private sharedData = new BehaviorSubject({});
  public toastSubject = new Subject<ToastMessage>();

  constructor(
    private httpClient: HttpClient,
    private router: Router
  ) { }


  private handleError(error: HttpErrorResponse) {
    let errorMessage: any = '';
    if (error.status === 400) {
      errorMessage = 'Bad Request (400)';
    } else if (error.status === 401) {
      errorMessage = 'Unauthorized (401)';
      sessionStorage.setItem(
      environment.storageName,
        JSON.stringify({})
      );
      this.router.navigate(['/'], { queryParams: { redirectTo: '' } });
    } else {
      errorMessage = `Error: ${error.message}`;
    }
    this.showToast({
      severity: 'error',
      detail: errorMessage ?? 'Something went wrong!',
    })
    return throwError(() => ({
      success: false,
      error: errorMessage,
      status: error.status,
    }));
  }

  private getToken() {
    let localStorageData = JSON.parse(
      sessionStorage.getItem(environment.storageName) || '{}'
    );
    if (localStorageData && localStorageData.token) {
      return localStorageData.token ?? null;
    }
    return null;
  }

  /** to get data this.sharedService.getData().subscribe((data: any) => {}) **/
  public getData() {
    let storedData = sessionStorage.getItem(environment.storageName);
    this.sharedData.next(JSON.parse(storedData || '{}'));
    return this.sharedData.asObservable();
  }

  /** to insert data this.sharedService.insertData({ key: 'name', val: response.data }) **/
  public insertData(data: any) {
    this.sharedData.next({
      ...this.sharedData.getValue(),
      [data.key]: data.val,
    });
    sessionStorage.setItem(
     environment.storageName,
      JSON.stringify(this.sharedData.value)
    );
  }

  /** Get Request with auth Token **/
  public sendGetRequest(target: string, params?: any): Observable<any> {
    let token = this.getToken();
    const headersConfig: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headersConfig['Authorization'] = `Bearer ${token}`;
    }
    const headers_object = new HttpHeaders(headersConfig);

    const httpOptions = {
      headers: headers_object,
    };
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        httpParams = httpParams.set(key, params[key]);
      });
    }
    return this.httpClient
      .get<any>(environment.apiUrl + target, {
        params: httpParams,
        ...httpOptions,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** Post Request with token auth **/
  public sendPostRequest(target: string, data: any): Observable<any> {
    let token = this.getToken();
    const headers_object = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    const httpOptions = {
      headers: headers_object,
    };
    return this.httpClient
      .post<any>(environment.apiUrl + target, data, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** Delete Request with token auth **/
  public sendDeleteRequest(target: string): Observable<any> {
    let token = this.getToken();
    const headers_object = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    const httpOptions = {
      headers: headers_object,
    };

    return this.httpClient
      .delete<any>(environment.apiUrl + target, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** Put Request with token auth **/
  public sendPutRequest(target: string, data: any): Observable<any> {
    let token = this.getToken();
    const headers_object = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    const httpOptions = {
      headers: headers_object,
    };

    return this.httpClient
      .put<any>(environment.apiUrl + target, data, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  public sendPatchRequest(target: string, data: any): Observable<any> {
    let token = this.getToken();
    const headers_object = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    const httpOptions = {
      headers: headers_object,
    };

    return this.httpClient
      .patch<any>(environment.apiUrl + target, data, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }

  public sendDownloadRequest(target: string) {
    let token = this.getToken();
    const headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
    });

    this.httpClient
      .get(target, {
        headers,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .subscribe({
        next: (response) => {
          const blob = response.body as Blob;
          const a = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          a.href = objectUrl;
          a.download = 'downloaded_file.png';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);
        },
        error: (error) => {
          console.error('Error downloading file:', error);
        }
      });
  }

  public getBlob(target: string, jwtToken?: string): Observable<any> {
    if (!jwtToken) {
      jwtToken = this.getToken(); // Your own method to fetch token
    }

    const headers_object = new HttpHeaders({
      Authorization: `Bearer ${jwtToken}`,
    });

    const httpOptions = {
      headers: headers_object,
      responseType: 'blob' as 'json', // Important: cast to 'json' for TypeScript
    };

    return this.httpClient
      .get<Blob>(environment.apiUrl + target, httpOptions)
      .pipe(catchError((error) => this.handleError(error)));
  }



  // ===========================================================================================
  // Toast Notification Service
  // ===========================================================================================


  showToast(toastMessage: ToastMessage) {
    this.toastSubject.next(toastMessage);
  }

  getToastSubject(): Observable<ToastMessage> {
    return this.toastSubject.asObservable();
  }


  // ===========================================================================================
  // Common Utility Functions
  // ===========================================================================================

  createDummyArray(length: number): any[] {
    return Array.from({ length }).map((_, i) => `Item #${i}`);
  }


}
